import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { DocumentNode, WatchQueryFetchPolicy } from '@apollo/client/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'environments/environment';
import { DebugLogger } from 'app/shared/utils/debugger-console';

@Injectable({
  providedIn: 'root',
})
export class GraphqlHostService {
  private readonly apolloProvider = inject(Apollo);

  loading = true;
  queryResultData: unknown;
  context: Record<string, never> | null = null;
  onQueryLoadedChange: BehaviorSubject<unknown> = new BehaviorSubject<unknown>({});

  getQueryResults<T = unknown>(
    serverName: string,
    graphQuery: DocumentNode,
    vars?: Record<string, unknown>,
    optionalContext?: { context: Record<string, never> },
    policy?: WatchQueryFetchPolicy
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      if (optionalContext) {
        this.context = optionalContext.context;
      }

      this.apolloProvider
        .use(serverName)
        .watchQuery<T>({
          fetchPolicy: policy,
          query: graphQuery,
          variables: vars,
          context: this.context ?? undefined,
        })
        .valueChanges.subscribe({
          next: (response): void => {
            resolve(response.data as T);
          },
          error: (err: Error): void => {
            reject(new Error(err.message));
          },
          complete: () => {
            if (environment.env !== 'prod') {
              DebugLogger.log('Query completed');
            }
          },
        });
    });
  }

  etMutationResults<T = unknown>(
    serverName: string,
    graphQuery: DocumentNode,
    vars: Record<string, unknown>
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.apolloProvider
        .use(serverName)
        .mutate<T>({
          mutation: graphQuery,
          variables: vars,
        })
        .subscribe({
          next: (response): void => {
            this.setReturningData(response);
            resolve(response.data as T);
          },
          error: (err: Error): void => {
            const withErrors = {
              error: `${err.message}, dest: ${graphQuery.definitions?.[0] ?? 'unknown'}`,
            };
            DebugLogger.error(withErrors);
            reject(new Error(err.message));
          },
        });
    });
  }

  setReturningData(response: { data?: unknown }): void {
    if (response.data) {
      this.queryResultData = response.data;
      this.onQueryLoadedChange.next(this.queryResultData);
    }
  }
}
